import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { takeLandingLanguage } from './landingLanguage';

// Import translations
import en from './locales/en.json';
import es from './locales/es.json';
import tr from './locales/tr.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import ar from './locales/ar.json';
import bg from './locales/bg.json';
import cs from './locales/cs.json';
import da from './locales/da.json';
import el from './locales/el.json';
import fi from './locales/fi.json';
import he from './locales/he.json';
import hi from './locales/hi.json';
import hu from './locales/hu.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import zh from './locales/zh.json';
import ru from './locales/ru.json';
import id from './locales/id.json';
import lt from './locales/lt.json';
import nl from './locales/nl.json';
import th from './locales/th.json';
import uk from './locales/uk.json';
import vi from './locales/vi.json';
import no from './locales/no.json';
import pl from './locales/pl.json';
import ro from './locales/ro.json';
import sv from './locales/sv.json';

// All supported language codes (matching mobile app)
export const SUPPORTED_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'zh', 'ar', 'tr', 'ru',
  'bg', 'cs', 'da', 'el', 'fi', 'he', 'hi', 'hu', 'id', 'lt', 'nl', 'no',
  'pl', 'ro', 'sv', 'th', 'uk', 'vi'
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Resources - only include languages with translation files
// Other languages will fall back to English
const resources = {
  en: { translation: en },
  es: { translation: es },
  tr: { translation: tr },
  fr: { translation: fr },
  de: { translation: de },
  pt: { translation: pt },
  ar: { translation: ar },
  bg: { translation: bg },
  cs: { translation: cs },
  da: { translation: da },
  el: { translation: el },
  fi: { translation: fi },
  he: { translation: he },
  hi: { translation: hi },
  hu: { translation: hu },
  id: { translation: id },
  it: { translation: it },
  ja: { translation: ja },
  ko: { translation: ko },
  lt: { translation: lt },
  nl: { translation: nl },
  ru: { translation: ru },
  th: { translation: th },
  uk: { translation: uk },
  vi: { translation: vi },
  zh: { translation: zh },
  no: { translation: no },
  pl: { translation: pl },
  ro: { translation: ro },
  sv: { translation: sv },
};

// Get stored language from localStorage or null
const getStoredLanguage = (): string | null => {
  try {
    const stored = localStorage.getItem('vivideo-settings');
    if (stored) {
      const settings = JSON.parse(stored);
      return settings.state?.language || null;
    }
  } catch (e) {
    console.error('Error reading stored language:', e);
  }
  return null;
};

// The language the visitor chose on the landing (`?lang=tr` from a /tr page), saved as their language before anything
// reads one (owner 2026-10-26; ./landingLanguage.ts). Absent: the stored language, else the browser's, as before.
const landingChoice = takeLandingLanguage(SUPPORTED_LANGUAGES);

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    
    // Use stored language first, then detect from browser
    lng: landingChoice || getStoredLanguage() || undefined,
    
    // Language detection options
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Keys for localStorage
      lookupLocalStorage: 'i18nextLng',
      // Cache the detected language
      caches: ['localStorage'],
    },
    
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Don't load missing translations for unsupported languages
    // They will just use the fallback (English)
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
  });

// Function to change language (called from settings store)
export const changeLanguage = (lang: string) => {
  // Normalize language code (e.g., 'en-US' -> 'en')
  const normalizedLang = lang.split('-')[0];
  
  // Check if the language is supported
  if (SUPPORTED_LANGUAGES.includes(normalizedLang as SupportedLanguage)) {
    i18n.changeLanguage(normalizedLang);
  } else {
    // Fall back to English for unsupported languages
    i18n.changeLanguage('en');
  }
};

// Get the browser's preferred language (normalized)
export const getBrowserLanguage = (): SupportedLanguage => {
  const browserLang = navigator.language.split('-')[0];
  if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }
  return 'en';
};

export default i18n;
